/**
 * Arps decline math for the asset workflow UI — mirrors asset-core/d2d_asset/arps.py
 * (rates per day, t in years, nominal declines; modified hyperbolic switches to the
 * terminal decline `dmin` once the instantaneous decline reaches it).
 */
export const DAYS_PER_YEAR = 365.25;
const B_EPS = 1e-6;

export const nominalFromEffective = (de) => (de > 0 ? -Math.log(1 - de) : 0);
export const effectiveFromNominal = (d) => (d > 0 ? 1 - Math.exp(-d) : 0);
export function nominalFromSecant(des, b) {
  if (!(des > 0)) return 0;
  if (b < B_EPS) return nominalFromEffective(des);
  return ((1 - des) ** -b - 1) / b;
}
export function secantFromNominal(di, b) {
  if (!(di > 0)) return 0;
  if (b < B_EPS) return effectiveFromNominal(di);
  return 1 - (1 + b * di) ** (-1 / b);
}

function switchTime(di, b, dmin) {
  if (!(dmin > 0) || b < B_EPS || di <= dmin) return null;
  return (di / dmin - 1) / (b * di);
}

export function rate(t, { qi, di, b, dmin }) {
  if (t <= 0) return qi;
  if (b < B_EPS) return qi * Math.exp(-di * t);
  const ts = switchTime(di, b, dmin);
  if (ts != null && t > ts) {
    const qs = qi / (1 + b * di * ts) ** (1 / b);
    return qs * Math.exp(-dmin * (t - ts));
  }
  return qi / (1 + b * di * t) ** (1 / b);
}

export function cum(t, p) {
  const { qi, di, b, dmin } = p;
  if (t <= 0 || !(qi > 0)) return 0;
  if (!(di > 0)) return qi * t * DAYS_PER_YEAR;
  if (b < B_EPS) return (qi / di) * (1 - Math.exp(-di * t)) * DAYS_PER_YEAR;
  const ts = switchTime(di, b, dmin);
  if (ts != null && t > ts) {
    return cum(ts, { qi, di, b }) + cum(t - ts, { qi: rate(ts, { qi, di, b }), di: dmin, b: 0 });
  }
  if (Math.abs(b - 1) < B_EPS) return (qi / di) * Math.log(1 + di * t) * DAYS_PER_YEAR;
  return (qi / ((1 - b) * di)) * (1 - (1 + b * di * t) ** (1 - 1 / b)) * DAYS_PER_YEAR;
}

export function addMonths(ym, n) {
  const y = Number(ym.slice(0, 4));
  const m = Number(ym.slice(5, 7));
  const t = y * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
}

export function monthsBetween(a, b) {
  return (Number(b.slice(0, 4)) - Number(a.slice(0, 4))) * 12 + Number(b.slice(5, 7)) - Number(a.slice(5, 7));
}

/** Normalise a fit / ARIES / override param set to {qi, di, b, dmin, t0}. */
export function toParams(src) {
  if (!src) return null;
  if (src.diEffPct != null && src.di == null) {
    const b = Number(src.b) || 0;
    return {
      qi: Number(src.qi),
      b,
      di: nominalFromSecant(src.diEffPct / 100, b),
      dmin: nominalFromEffective((src.dminEffPct ?? 6) / 100),
      t0: src.t0 || null,
    };
  }
  return { qi: src.qi, di: src.di, b: src.b || 0, dmin: src.b > 0 ? src.dmin : null, t0: src.t0 || null };
}

/**
 * Monthly forecast [{date, rate, volume}] starting at `start` (YYYY-MM).
 * `t0` = month the params' t=0 refers to (fits: window start; ARIES/override: start).
 */
export function forecast(params, { start, months = 360, qLimit = 0 } = {}) {
  const p = toParams(params);
  if (!p || !(p.qi > 0) || !start) return [];
  const off = p.t0 ? monthsBetween(p.t0, start) : 0;
  const out = [];
  for (let i = 0; i < months; i += 1) {
    const ta = (off + i) / 12;
    const tb = (off + i + 1) / 12;
    const r = rate((ta + tb) / 2, p);
    if (r < qLimit) break;
    out.push({ date: addMonths(start, i), rate: r, volume: Math.max(cum(tb, p) - cum(ta, p), 0) });
  }
  return out;
}

/** Asset-core economic limit: oil 1.0 bbl/d, gas 5.0 Mcf/d. */
export function defaultQLimit(product) {
  const p = String(product || '').toLowerCase();
  if (p === 'oil' || p === 'ngl' || p === 'condensate' || p.includes('bbl')) return 1;
  return 5;
}

export function eurFrom(params, { start, qLimit, years = 50, product } = {}) {
  const limit = qLimit != null ? qLimit : defaultQLimit(product ?? params?.product);
  return forecast(params, { start, months: years * 12, qLimit: limit }).reduce((s, r) => s + r.volume, 0);
}
